import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MailQuestion } from "lucide-react";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { api, formatApiError, gbp } from "../lib/api";
import { useShop } from "../context/ShopContext";
import { FadeUp } from "../components/Reveal";

const EMPTY = { name: "", email: "", phone: "", address1: "", address2: "", city: "", postcode: "", country: "Isle of Man" };

export default function Checkout() {
  const { cart, subtotal, clearCart } = useShop();
  const [shipping, setShipping] = useState([]);
  const [paypalClientId, setPaypalClientId] = useState("");
  const [method, setMethod] = useState("uk-signed");
  const [customer, setCustomer] = useState(EMPTY);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/config").then(({ data }) => {
      setShipping(data.shipping);
      setPaypalClientId(data.paypal_client_id || "");
    }).catch(() => {});
    // pre-fill details for signed-in customers
    if (localStorage.getItem("cg_customer_token")) {
      api.get("/auth/me").then(({ data }) => {
        setCustomer((c) => ({
          ...c,
          name: c.name || data.name || "",
          email: c.email || data.email || "",
          address1: c.address1 || data.address?.address1 || "",
          address2: c.address2 || data.address?.address2 || "",
          city: c.city || data.address?.city || "",
          postcode: c.postcode || data.address?.postcode || "",
          country: c.country !== "Isle of Man" ? c.country : data.address?.country || c.country,
        }));
      }).catch(() => {});
    }
  }, []);

  const delivery = shipping.find((s) => s.key === method);
  const [discountInput, setDiscountInput] = useState("");
  const [discount, setDiscount] = useState(null);
  const [discountError, setDiscountError] = useState("");
  const discountAmount = discount ? Math.round(subtotal * discount.percent) / 100 : 0;
  const total = subtotal - discountAmount + (delivery?.price || 0);

  const applyDiscount = async () => {
    setDiscountError("");
    if (!discountInput.trim()) return;
    try {
      const { data } = await api.post("/discounts/validate", { code: discountInput.trim() });
      setDiscount(data);
      toast.success(`${data.percent}% discount applied`);
    } catch (e) {
      setDiscount(null);
      setDiscountError(formatApiError(e));
    }
  };

  const set = (k) => (e) => setCustomer((c) => ({ ...c, [k]: e.target.value }));

  const payload = () => ({
    items: cart.map((i) => ({
      product_id: i.product_id,
      qty: i.qty,
      size: i.size,
      personalisation: i.personalisation || null,
      delivery_mode: i.delivery_mode || null,
      recipient_email: i.recipient_email || null,
      metal: i.metal || null,
      chain: i.chain || null,
      width: i.width || null,
    })),
    customer,
    delivery_method: method,
    message,
    origin_url: window.location.origin,
    discount_code: discount?.code || null,
  });

  const validate = () => {
    if (!customer.name.trim() || !customer.email.trim()) {
      toast.error("Please add your name and email.");
      return false;
    }
    if (!cart.length) {
      toast.error("Your basket is empty.");
      return false;
    }
    return true;
  };

  const placeEnquiry = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/orders/enquiry", payload());
      localStorage.setItem("cg_last_order", JSON.stringify({ order_number: data.order_number, total: data.total, enquiry: true }));
      clearCart();
      navigate("/order/success?enquiry=1");
    } catch (e) {
      toast.error(formatApiError(e));
      setLoading(false);
    }
  };

  if (!cart.length && !loading) {
    return (
      <main className="pt-48 pb-32 text-center" data-testid="checkout-empty">
        <h1 className="font-serif text-4xl text-ivory mb-4">Your basket is empty</h1>
        <Link to="/shop/all" className="text-gold text-xs uppercase tracking-[0.25em] hover:text-gold-glow">Browse the shop</Link>
      </main>
    );
  }

  const field = "w-full bg-ink-2 border border-white/10 focus:border-gold outline-none px-4 py-3.5 text-sm text-ivory placeholder:text-stone-mist/50 transition-colors";

  return (
    <main className="pt-36 lg:pt-44 pb-24" data-testid="checkout">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeUp>
          <h1 className="font-serif text-4xl sm:text-5xl text-ivory font-light mb-12">Checkout</h1>
        </FadeUp>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-12">
          <div className="space-y-10">
            <FadeUp>
              <h2 className="text-xs uppercase tracking-[0.25em] text-gold mb-5 font-medium">Your details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input value={customer.name} onChange={set("name")} placeholder="Full name *" className={field} data-testid="checkout-name" />
                <input value={customer.email} onChange={set("email")} type="email" placeholder="Email *" className={field} data-testid="checkout-email" />
                <input value={customer.phone} onChange={set("phone")} placeholder="Telephone" className={field} data-testid="checkout-phone" />
                <input value={customer.address1} onChange={set("address1")} placeholder="Address line 1" className={field} data-testid="checkout-address1" />
                <input value={customer.address2} onChange={set("address2")} placeholder="Address line 2" className={field} data-testid="checkout-address2" />
                <input value={customer.city} onChange={set("city")} placeholder="Town / City" className={field} data-testid="checkout-city" />
                <input value={customer.postcode} onChange={set("postcode")} placeholder="Postcode" className={field} data-testid="checkout-postcode" />
                <input value={customer.country} onChange={set("country")} placeholder="Country" className={field} data-testid="checkout-country" />
              </div>
            </FadeUp>

            <FadeUp delay={0.05}>
              <h2 className="text-xs uppercase tracking-[0.25em] text-gold mb-5 font-medium">Delivery method</h2>
              <div className="space-y-3" data-testid="delivery-methods">
                {shipping.map((s) => (
                  <label
                    key={s.key}
                    className={`flex items-center justify-between gap-4 border p-4 cursor-pointer transition-colors ${
                      method === s.key ? "border-gold bg-gold/5" : "border-white/10 hover:border-gold/40"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input type="radio" name="delivery" checked={method === s.key} onChange={() => setMethod(s.key)} data-testid={`delivery-${s.key}`} className="accent-[#C5A059]" />
                      <div>
                        <p className="text-ivory text-sm">{s.label}</p>
                        <p className="text-stone-mist text-xs mt-0.5">{s.note}</p>
                      </div>
                    </div>
                    <span className="font-mono text-sm text-gold whitespace-nowrap">{gbp(s.price)}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-stone-mist mt-4 leading-relaxed">
                Larger or more expensive items may require separate delivery arrangements and a supplement may apply.
                Mention any special requirements below. As we are based on an island, weather may occasionally affect
                postal services.
              </p>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Special delivery instructions or a note with your order (optional)"
                rows={3}
                className={`${field} mt-4`}
                data-testid="checkout-message"
              />
            </FadeUp>
          </div>

          <FadeUp delay={0.1}>
            <div className="border border-gold/20 bg-ink-2/60 p-7 sticky top-36" data-testid="order-summary">
              <h2 className="text-xs uppercase tracking-[0.25em] text-gold mb-6 font-medium">Order summary</h2>
              <div className="space-y-4 mb-6 max-h-64 overflow-y-auto pr-1">
                {cart.map((item) => (
                  <div key={item.key} className="flex gap-3">
                    {item.image && <img src={item.image} alt="" className="w-12 h-14 object-cover border border-white/5" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-ivory text-sm font-light leading-snug">{item.name}</p>
                      <p className="text-stone-mist text-xs mt-0.5">
                        Qty {item.qty}
                        {item.width ? ` · ${item.width}` : ""}
                        {item.size ? ` · ${item.size.startsWith("£") ? item.size : `Size ${item.size}`}` : ""}
                      </p>
                      {item.personalisation && <p className="text-stone-mist/70 text-[11px] mt-0.5 italic">{item.personalisation}</p>}
                    </div>
                    <p className="font-mono text-xs text-gold pt-1">{gbp(item.price * item.qty)}</p>
                  </div>
                ))}
              </div>
              <div className="mb-5">
                <div className="flex gap-2">
                  <input
                    value={discountInput}
                    onChange={(e) => setDiscountInput(e.target.value.toUpperCase())}
                    placeholder="Discount code"
                    data-testid="discount-input"
                    className="flex-1 bg-ink border border-white/10 focus:border-gold outline-none px-4 py-3 text-sm text-ivory placeholder:text-stone-mist/50 font-mono tracking-wider transition-colors"
                  />
                  <button
                    onClick={applyDiscount}
                    data-testid="discount-apply"
                    className="border border-gold/40 text-gold px-5 text-xs uppercase tracking-[0.2em] hover:bg-gold hover:text-ink transition-colors"
                  >
                    Apply
                  </button>
                </div>
                {discountError && <p className="text-red-400 text-xs mt-2" data-testid="discount-error">{discountError}</p>}
                {discount && (
                  <p className="text-emerald-400 text-xs mt-2 flex items-center justify-between" data-testid="discount-applied">
                    <span>{discount.code} — {discount.percent}% off applied</span>
                    <button onClick={() => { setDiscount(null); setDiscountInput(""); }} className="text-stone-mist hover:text-red-400">Remove</button>
                  </p>
                )}
              </div>
              <div className="border-t border-white/10 pt-4 space-y-2 text-sm">
                <p className="flex justify-between text-stone-mist"><span>Subtotal</span><span className="font-mono">{gbp(subtotal)}</span></p>
                {discount && (
                  <p className="flex justify-between text-emerald-400">
                    <span>Discount ({discount.percent}%)</span>
                    <span className="font-mono" data-testid="discount-amount">−{gbp(discountAmount)}</span>
                  </p>
                )}
                <p className="flex justify-between text-stone-mist"><span>Delivery</span><span className="font-mono">{gbp(delivery?.price || 0)}</span></p>
                <p className="flex justify-between text-ivory text-base pt-2 border-t border-white/10">
                  <span>Total</span><span className="font-mono text-gold" data-testid="order-total">{gbp(total)}</span>
                </p>
              </div>
              {paypalClientId && (
                <>
                  <div className="mt-7" data-testid="paypal-buttons">
                    <PayPalScriptProvider options={{ clientId: paypalClientId, currency: "GBP", intent: "capture" }}>
                      <PayPalButtons
                        style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal" }}
                        forceReRender={[total]}
                        createOrder={async () => {
                          if (!validate()) throw new Error("checkout details incomplete");
                          const { data } = await api.post("/checkout/paypal/create", payload());
                          localStorage.setItem("cg_last_order", JSON.stringify({ order_number: data.order_number, total }));
                          return data.paypal_order_id;
                        }}
                        onApprove={async (data) => {
                          try {
                            await api.post("/checkout/paypal/capture", { paypal_order_id: data.orderID });
                            clearCart();
                            navigate(`/order/success?session_id=${data.orderID}`);
                          } catch (e) {
                            toast.error(formatApiError(e));
                          }
                        }}
                        onError={() => toast.error("PayPal could not complete the payment — please try again or pay by card.")}
                        onCancel={() => toast.info("PayPal checkout cancelled — no payment was taken.")}
                      />
                    </PayPalScriptProvider>
                  </div>
                </>
              )}
              <button
                onClick={placeEnquiry}
                disabled={loading}
                data-testid="place-enquiry"
                className="w-full mt-3 border border-gold/40 text-gold py-4 text-xs uppercase tracking-[0.25em] hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <MailQuestion className="w-4 h-4" /> Place as Order Enquiry
              </button>
              <p className="text-[11px] text-stone-mist mt-4 leading-relaxed">
                Pay securely with PayPal, or by debit/credit card via PayPal — no PayPal account needed. With an order
                enquiry, we confirm availability and acceptance by email in accordance with our terms.
              </p>
            </div>
          </FadeUp>
        </div>
      </div>
    </main>
  );
}
